import { ContentContainer } from "@/components/primitives/ContentContainer";
import { ChapterSection } from "@/components/primitives/ChapterLayout";
import { VideoExhibit } from "@/components/primitives/VideoExhibit";
import { ChevronFlow } from "@/components/primitives/ChevronFlow";
import { HorizontalBarChart } from "@/components/primitives/HorizontalBarChart";
import { SegmentedBar } from "@/components/primitives/SegmentedBar";
import { Expandable } from "@/components/primitives/Expandable";
import { PAPER_SECTIONS } from "@/lib/sections";

/* ─── Content constants ──────────────────────────────────────────────── */

const HAEVN_FLOW = [
  {
    label: "Complete Survey",
    description:
      "A single, adaptive survey. Members define who they are, what they are looking for, and the conditions under which a connection is most likely to succeed.",
  },
  {
    label: "Compatibility Analysis",
    description:
      "HAEVN evaluates compatibility across the relevant local network — quietly, in the background, without the member having to search.",
  },
  {
    label: "Match Monday Update",
    description:
      "Every Match Monday, HAEVN provides eligible members with a Match, a Recommendation, or an update that the search continues. Weekly evaluation continues even in the weeks a member does not receive an introduction.",
  },
  {
    label: "Introduction When Qualified",
    description:
      "Matches are introductions that meet HAEVN's 80% compatibility threshold, subject to eligibility and matching requirements. Recommendations surface where the network offers useful context. Compatibility standards are not lowered to fill a delivery quota.",
  },
  {
    label: "Connect",
    description:
      "Members choose whether to engage. Conversations start with meaningful context already established, not with a cold profile.",
  },
  {
    label: "Meet",
    description:
      "Introductions move into the real world — often through HAEVN's growing network of trusted local venues and community partners.",
  },
];

const DIGITAL_DATING_WAVES = [
  {
    wave: "First Wave",
    era: "1995 – 2012",
    label: "Search-Based Digital Dating",
    innovation: "Expanded access.",
    examples: ["Match.com", "eHarmony"],
  },
  {
    wave: "Second Wave",
    era: "2012 – Present",
    label: "Swipe-Based Digital Dating",
    innovation: "Reduced friction.",
    examples: ["Tinder", "Bumble", "Hinge"],
  },
  {
    wave: "Third Wave",
    era: "Emerging",
    label: "Relationship Networks",
    innovation: "Reduced uncertainty.",
    examples: ["HAEVN"],
  },
];

const REAL_PEOPLE_VIDEO =
  "https://customer-assets.emergentagent.com/job_investor-foundation/artifacts/ag8zj55l_SreetTalk%20HAEVN%20Mashup.mp4";

const PRODUCT_OVERVIEW_VIDEO =
  "https://customer-assets.emergentagent.com/job_investor-foundation/artifacts/hgfucrsf_HAEVN%20Overview%20042026%28COMPRESSED%29.mp4";

const MARKET_ENGAGEMENT_KPIS = [
  {
    id: "first-attempt",
    headline: "~70% Complete on Their First Attempt",
    body: "Since the network went live, approximately 70% of people who begin HAEVN's guided discovery process complete it on their first attempt, based on our running average. A thoughtful onboarding experience takes roughly fifteen to twenty minutes to complete. In an era of short attention spans and endless scrolling, that's a meaningful signal that people believe the potential outcome is worth the investment.",
  },
  {
    id: "return-later",
    headline: "Many Members Return to Finish Later",
    body: "Not everyone has fifteen to twenty uninterrupted minutes when they first discover HAEVN. Many members begin the onboarding process while browsing social media, during a break, or between other activities, then return later to finish what they started. Reminder emails sent after 24 and 72 hours successfully recover a meaningful portion of these in-progress surveys, reinforcing that thoughtful onboarding is often completed over multiple sessions rather than abandoned altogether.",
  },
  {
    id: "share-rate",
    headline: "50%+ Share Rate",
    body: "Since the network went live, more than half of completed members have voluntarily shared HAEVN with others, based on our running average. People rarely recommend experiences they feel indifferent about. Sharing suggests optimism about what HAEVN represents and confidence that others may benefit from it as well. Sharing is a distinct measure from referral-attributed completions; not every share produces a new member.",
  },
];

const AGE_DISTRIBUTION = [
  { label: "25–34", value: 25 },
  { label: "35–44", value: 24 },
  { label: "55+",   value: 20 },
  { label: "45–54", value: 20 },
  { label: "18–24", value: 10 },
];

const GENDER_SEGMENTS = [
  { label: "Men",            value: 64 },
  { label: "Women",          value: 31 },
  { label: "Gender Diverse", value: 5  },
];

const ORIENTATION_SEGMENTS = [
  { label: "Straight",       value: 79 },
  { label: "LGBTQ+ & Other", value: 21 },
];

const RELATIONSHIP_INTENTIONS = [
  {
    id: "relationship",
    value: "~50%",
    label: "Looking for a Relationship",
    includes: ["Long-term", "Short-term"],
  },
  {
    id: "casual",
    value: "~36%",
    label: "Casual Dating",
    includes: ["Casual", "Friends with Benefits"],
  },
  {
    id: "community",
    value: "~14%",
    label: "Friendship & Community",
    includes: ["Community", "Group", "Other"],
  },
];

const MARKETING_PRINCIPLES = [
  {
    id: "existing-objectives",
    title: "Advances Existing Objectives",
    body: "The Growth Partner model supports objectives many organizations already pursue — brand affinity, community participation, earned media, authentic storytelling, and differentiation. Rather than requiring a new set of goals, it advances the ones you're already investing behind.",
  },
  {
    id: "long-term-capability",
    title: "Builds Long-Term Capability",
    body: "The objective is not another campaign. It is participation in infrastructure whose value compounds over time — creating capabilities and relationships that continue to generate value long after any single activation ends.",
  },
  {
    id: "position-early",
    title: "Positions Organizations Early",
    body: "Participation while a category is still being defined creates strategic opportunities that cannot be recreated later. This is not a guarantee of category leadership. It is the opportunity to help shape the standards, narrative, and expectations of an emerging one.",
  },
  {
    id: "modest-commitment",
    title: "Begins With Modest Commitment",
    body: "The model intentionally begins with low-risk participation that allows both organizations to learn before expanding the relationship. That structure reflects HAEVN's broader philosophy: reduce uncertainty before increasing commitment.",
  },
  {
    id: "organizational-values",
    title: "Reflects Organizational Values",
    body: "Participation communicates something about how an organization chooses to show up within the communities it serves. It signals a view of relationships, community, and human connection as worthy of long-term investment.",
  },
];

const PARTNERSHIP_PHASES = [
  {
    id: "awareness",
    title: "Establish Awareness",
    objective: "Introduce the idea",
    question: "Does this resonate?",
    body: "The first step introduces the idea through channels both organizations already trust. The purpose is to generate learning — how consumers respond, which parts of the story resonate — not to launch a campaign.",
  },
  {
    id: "measure",
    title: "Measure What Matters",
    objective: "Evaluate evidence",
    question: "Is engagement meaningful?",
    body: "Both organizations evaluate the response using observable behavior rather than vanity metrics. The purpose is not to prove success. It is to determine whether the evidence justifies further investment.",
  },
  {
    id: "story",
    title: "Expand the Story",
    objective: "Build credibility",
    question: "Is there something worth amplifying?",
    body: "When early evidence supports it, the partnership shifts from announcement to storytelling — narratives grounded in what has actually happened rather than what was projected.",
  },
  {
    id: "community",
    title: "Build Community Together",
    objective: "Deepen collaboration",
    question: "Can we create greater value together?",
    body: "An initial collaboration may reach a broad audience or a relevant local community. Early community participation can help generate the evidence that supports deeper local activity later. Larger community programs follow demonstrated momentum rather than preceding it.",
  },
  {
    id: "expand",
    title: "Expand with Confidence",
    objective: "Scale intentionally",
    question: "Has the relationship earned expansion?",
    body: "If earlier stages have created sustained value, expansion becomes an informed decision — grounded in evidence rather than aspiration. There is intentionally no predetermined roadmap; every partnership evolves differently.",
  },
];

const EXECUTIVE_QUESTIONS = [
  {
    id: "sponsorship",
    q: "Is this simply another sponsorship opportunity?",
    a: "Not quite. Sponsorships exchange financial support for visibility, typically measured during a defined window. A Growth Partnership is designed to give your audience something valuable — a modern matchmaking service — while creating agreed measures of participation, engagement, and local activity that inform whether to invest further. Visibility can be one component of the collaboration alongside useful audience engagement and community activity.",
  },
  {
    id: "communications-first",
    q: "Why begin with communications rather than a larger marketing investment?",
    a: "Because both organizations should have the opportunity to learn before making larger commitments. Beginning with capabilities most organizations already possess is an efficient way to observe how the audience responds. The initial objective isn't scale. It's evidence.",
  },
  {
    id: "why-early",
    q: "Why would an established brand choose to participate this early?",
    a: "Organizations that participate while a category is being defined have an opportunity to help shape it — the standards, the narrative, the expectations. That opportunity exists during a category's formative stages. Participation remains possible after a category matures; helping shape it does not.",
  },
  {
    id: "assumptions",
    q: "What assumptions does this opportunity depend on?",
    a: "Two, primarily. First, that consumer behavior is shifting toward a more intentional approach to relationships — a trend supported by HAEVN's early market signals. Second, that infrastructure built with the right partners can develop value over time. The Growth Partner model is designed so that the initial decision can be evaluated against those assumptions before larger commitments are made.",
  },
  {
    id: "what-decision",
    q: "What decision are we actually making?",
    a: "The decision is whether offering your audience a modern matchmaking service — and building measurable local participation around it — advances your organization's objectives. HAEVN provides the service and community infrastructure. Your organization provides trusted reach and local relationships. The first collaboration is designed to test that fit before either side commits further.",
  },
  {
    id: "measure-success",
    q: "How will success be measured?",
    a: "Through measures agreed together before work begins, across three dimensions: value created for the Growth Partner, value created for HAEVN, and value created for the communities both organizations serve. Section 13 lists practical measures that can be selected for a given collaboration. These are measures to evaluate, not results already achieved.",
  },
  {
    id: "national-vs-local",
    q: "Does 'national reach' mean matching people indiscriminately across the country?",
    a: "No. National marketing reveals where demand is emerging. Matching remains grounded in relevant local networks and member preferences. Members can enter the process from anywhere in the country without waiting for a launch threshold, and HAEVN concentrates additional investment in Focus Cities where local participation demonstrates momentum. Focus City is an internal investment designation, not a condition of member eligibility.",
  },
];

/* ─── Reusable local subcomponents ───────────────────────────────────── */

const PullQuote = ({ children, testId }) => (
  <p
    data-testid={testId}
    className="mt-8 border-l-2 border-foreground pl-6 text-xl font-normal leading-snug tracking-tight text-foreground sm:text-2xl"
  >
    {children}
  </p>
);

const ExchangeDownArrow = () => (
  <svg
    width="12"
    height="14"
    viewBox="0 0 12 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 0V13M6 13L1 8M6 13L11 8"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhaseBlock = ({ number, title, children, testId }) => (
  <div
    data-testid={testId}
    className="grid grid-cols-1 gap-6 border-t border-border pt-8 lg:grid-cols-[160px_1fr] lg:gap-10"
  >
    <div className="flex flex-col gap-2">
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
        Phase {number}
      </span>
      <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
        {title}
      </h3>
    </div>
    <div className="flex max-w-3xl flex-col gap-4 text-base leading-relaxed text-foreground/85 sm:text-[17px] sm:leading-[1.65]">
      {children}
    </div>
  </div>
);

const SuccessDimension = ({ label, children, testId }) => (
  <div data-testid={testId} className="border-t border-border pt-8">
    <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
      {label}
    </div>
    <div className="mt-4 flex max-w-3xl flex-col gap-4 text-base leading-relaxed text-foreground/85 sm:text-[17px] sm:leading-[1.65]">
      {children}
    </div>
  </div>
);

/* ─── Helpers ────────────────────────────────────────────────────────── */

const sectionNumber = (id) => {
  const s = PAPER_SECTIONS.find((x) => x.id === id);
  return s ? s.number : "";
};

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function Paper() {
  return (
    <div data-testid="paper-root">
      {/* 01 · Introduction */}
      <section id="hero" data-paper-section data-testid="section-hero" className="scroll-mt-16">
        <ContentContainer padded={false}>
          <div className="py-16 sm:py-20 lg:py-24">
            {/* Document title */}
            <header className="mb-14 sm:mb-16 lg:mb-20" data-testid="brief-title">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                Growth Partner Executive Brief
              </div>
              <div className="mt-8 h-px w-full bg-border" aria-hidden="true" />
            </header>

            {/* Partner-value-first lead */}
            <div
              data-testid="hero-lead"
              className="max-w-3xl"
            >
              <h1
                data-testid="hero-headline"
                className="text-4xl font-medium tracking-tightest text-foreground sm:text-5xl lg:text-[56px] sm:leading-[1.02]"
              >
                Give people another reason to value your brand.
              </h1>

              <p
                data-testid="hero-subhead"
                className="mt-8 max-w-2xl text-lg leading-[1.55] text-foreground/85 sm:text-xl sm:leading-[1.5]"
              >
                Help your audience find compatible relationships in the
                communities you serve — and create meaningful opportunities
                for local engagement, brand connection, and storytelling.
              </p>

              <p
                data-testid="hero-body"
                className="mt-6 max-w-2xl text-[15px] leading-[1.7] text-foreground/75 sm:text-base sm:leading-[1.75]"
              >
                A HAEVN Growth Partnership connects your organization&apos;s
                reach and community relationships with a modern matchmaking
                service. Start through channels you already have, measure
                how your audience responds, and expand into local
                experiences where participation supports the next
                investment.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                <a
                  href="#invitation"
                  data-testid="hero-primary-cta"
                  className="inline-flex w-fit items-center gap-2 rounded-full border border-foreground bg-foreground px-6 py-3 text-sm font-medium tracking-tight text-background transition-colors hover:bg-foreground/90 sm:text-[15px]"
                >
                  Explore a Growth Partnership
                  <span aria-hidden="true">→</span>
                </a>
                <p className="text-sm leading-relaxed text-muted-foreground sm:text-[14px]">
                  Start with a focused collaboration, clear responsibilities,
                  and agreed measures of success.
                </p>
              </div>
            </div>

            {/* Three partner benefits — replaces prior Executive Snapshot */}
            <div
              data-testid="partner-benefits"
              className="mt-20 border-t border-border pt-10 sm:mt-24"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                What a Growth Partnership Offers Your Organization
              </div>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-foreground/85 sm:text-[17px] sm:leading-[1.65]">
                A Growth Partnership creates three practical opportunities.
                Present these to your audience, your leadership, and your
                communities as ways to advance objectives your organization
                is already pursuing.
              </p>

              <div
                data-testid="benefits-grid"
                className="mt-10 flex flex-col gap-4 sm:gap-5"
              >
                {[
                  {
                    id: "audience",
                    benefit: "A meaningful offering for your audience",
                    body: "Bring your audience a service designed to help people find compatible relationships. Give them something useful to engage with, while creating an opportunity to associate your brand with an experience that matters personally.",
                  },
                  {
                    id: "local",
                    benefit: "A tangible way to participate locally",
                    body: "Turn community commitment into opportunities for participation — from introducing HAEVN through trusted channels to developing local gatherings and Meetup Spot collaborations where momentum supports them.",
                  },
                  {
                    id: "evidence",
                    benefit: "Evidence to guide your next investment",
                    body: "See how participation develops through your collaboration and where local interest emerges. Use agreed reporting to decide whether to deepen activity, expand to additional markets, or adjust the approach.",
                  },
                ].map((card, i) => (
                  <div
                    key={card.id}
                    data-testid={`benefit-${card.id}`}
                    className="rounded-lg border border-border bg-surface/50 p-6 sm:p-8"
                  >
                    <div className="flex flex-col gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        Benefit {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="text-2xl font-medium tracking-tightest text-foreground sm:text-3xl lg:text-[32px] leading-[1.15]">
                        {card.benefit}
                      </h3>
                    </div>
                    <p className="mt-6 max-w-3xl text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.7]">
                      {card.body}
                    </p>
                  </div>
                ))}
              </div>

              <p
                data-testid="benefits-footnote"
                className="mt-8 max-w-3xl text-xs leading-relaxed text-muted-foreground sm:text-[13px]"
              >
                These are opportunities the partnership offers. They are
                not promises of guaranteed loyalty, customer acquisition,
                foot traffic, earned media, or relationship outcomes.
              </p>
            </div>

            {/* Strategy band — quiet editorial bridge into Section 02 */}
            <div
              data-testid="strategy-band"
              className="mt-20 border-t border-border pt-10 sm:mt-24"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Operating Principle
              </div>
              <p className="mt-4 max-w-3xl text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.2]">
                Market nationally. Build locally. Invest selectively.
              </p>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px] sm:leading-[1.7]">
                For Growth Partners, this means starting with your
                audience, identifying where participation grows, and
                directing deeper local investment where the response
                supports it.
              </p>
            </div>
          </div>
        </ContentContainer>
      </section>

      {/* 02 · What is HAEVN? */}
      <section
        id="what-is-haevn"
        data-paper-section
        data-testid="section-what-is-haevn"
        className="scroll-mt-16 border-y border-border bg-surface/40"
      >
        <div className="haevn-container py-24 sm:py-28 lg:py-32">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_1fr] lg:gap-16">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {sectionNumber("what-is-haevn")}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                The Product
              </span>
              <p className="mt-3 max-w-[180px] text-sm leading-relaxed text-muted-foreground">
                A two-minute overview of what HAEVN is and how it works.
              </p>
            </div>
            <div className="max-w-3xl">
              <h2
                className="text-3xl font-medium tracking-tightest text-foreground sm:text-4xl lg:text-[40px] leading-[1.08]"
                data-testid="section-what-is-haevn-title"
              >
                What is HAEVN?
              </h2>

              {/* Canonical definition */}
              <p
                data-testid="haevn-definition"
                className="mt-8 text-xl font-medium tracking-tight text-foreground sm:text-2xl sm:leading-[1.35]"
              >
                HAEVN is a modern matchmaking service connecting
                compatible people through a national network of local
                communities.
              </p>

              {/* Overview */}
              <div
                data-testid="haevn-overview"
                className="mt-8 flex flex-col gap-5 text-base leading-relaxed text-foreground/85 sm:text-[17px] sm:leading-[1.65]"
              >
                <p>
                  HAEVN begins with a single, guided discovery process
                  designed to understand far more than a traditional
                  dating profile. Through a thoughtful series of adaptive
                  questions, HAEVN uncovers who members are, what
                  they&apos;re looking for, and the conditions under
                  which a relationship is most likely to succeed.
                </p>
                <p className="text-foreground">
                  The goal isn&apos;t simply to understand individual
                  preferences. It&apos;s to understand the degree of
                  alignment between two people before they&apos;re ever
                  introduced.
                </p>
                <p>
                  Completing the survey begins an ongoing relationship
                  with HAEVN. Every Match Monday, HAEVN evaluates the
                  relevant local network and provides eligible members
                  with a <span className="text-foreground">Match</span>,
                  a <span className="text-foreground">Recommendation</span>,
                  or an update that the search continues. An
                  introduction is never guaranteed each week, and
                  compatibility standards are not lowered to fill a
                  delivery quota. Matches are introductions that meet
                  HAEVN&apos;s 80% compatibility threshold, subject to
                  existing eligibility and matching requirements.
                  Recommendations surface where the network offers
                  useful context. Members remain part of an active
                  service as their local network grows.
                </p>
              </div>

              {/* Product Workflow */}
              <div className="mt-16" data-testid="haevn-workflow">
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Product Workflow
                </div>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                  The experience, from first survey to first meeting.
                  Match Monday continues weekly, whether or not an
                  introduction qualifies in a given week. Hover any step
                  for detail.
                </p>
                <div className="mt-8">
                  <ChevronFlow steps={HAEVN_FLOW} testId="workflow-diagram" />
                </div>
              </div>

              {/* Independent Product Overview */}
              <div className="mt-16" data-testid="independent-overview">
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Independent Product Overview
                </div>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                  A third-party walkthrough of the HAEVN experience,
                  produced independently of the company.
                </p>
                <div className="mt-6">
                  <VideoExhibit
                    label="Independent Product Overview"
                    src={PRODUCT_OVERVIEW_VIDEO}
                    poster="/posters/overview-poster.jpg"
                    testId="video-independent"
                  />
                </div>
              </div>

              {/* Real People's First Reactions */}
              <div className="mt-16" data-testid="real-reactions">
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  Real People&apos;s First Reactions
                </div>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                  Authentic, unscripted first impressions from people
                  encountering HAEVN for the first time.
                </p>
                <div className="mt-6">
                  <VideoExhibit
                    label="Real People's First Reactions"
                    src={REAL_PEOPLE_VIDEO}
                    poster="/posters/street-poster.jpg"
                    testId="video-street"
                  />
                </div>
              </div>

              {/* What the collaboration includes */}
              <div
                className="mt-20 border-t border-border pt-10"
                data-testid="collaboration-includes"
              >
                <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  What the collaboration includes
                </div>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground/85 sm:text-[17px] sm:leading-[1.65]">
                  Each collaboration begins with a defined audience, a
                  practical activation, and a clear way to evaluate the
                  response. The scope is agreed together before work
                  begins.
                </p>

                <div
                  data-testid="collaboration-items"
                  className="mt-8 flex flex-col gap-3 sm:gap-4"
                >
                  {[
                    {
                      id: "audience-activation",
                      title: "Audience activation",
                      body: "The communications or activities through which the partner introduces HAEVN.",
                    },
                    {
                      id: "partner-recognition",
                      title: "Partner recognition",
                      body: "Where and how the partner's contribution will be acknowledged.",
                    },
                    {
                      id: "measurement",
                      title: "Measurement",
                      body: "The participation and engagement measures both organizations will use to evaluate the collaboration.",
                    },
                    {
                      id: "expansion",
                      title: "Expansion opportunities",
                      body: "The local experiences, storytelling, or additional markets the organizations may pursue if evidence supports them.",
                    },
                  ].map((item, i) => (
                    <div
                      key={item.id}
                      data-testid={`collab-item-${item.id}`}
                      className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface/50 p-5 sm:grid-cols-[56px_1fr] sm:items-baseline sm:gap-6 sm:p-6"
                    >
                      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="text-base font-medium tracking-tight text-foreground sm:text-lg">
                          {item.title}
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground/80 sm:text-[15px] sm:leading-[1.65]">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-6 max-w-2xl text-xs leading-relaxed text-muted-foreground sm:text-[13px]">
                  These are elements to agree in a proposal, not a claim
                  that HAEVN already offers a standardized package.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 · The Third Wave of Digital Dating */}
      <ChapterSection
        id="market-shift"
        number={sectionNumber("market-shift")}
        eyebrow="Digital Dating"
        title="The third wave of digital dating."
        question="Where is digital dating headed next?"
      >
        {/* Introductory framing — precedes the visual framework */}
        <p>
          Digital dating has evolved through two distinct generations,
          and we believe it is now entering a third. Each generation was
          defined by a different innovation, and each solved a different
          problem for the people who used it.
        </p>

        {/* Visual framework — the primary storytelling device */}
        <div
          data-testid="three-waves-framework"
          className="not-prose relative -mx-2 mt-2 flex flex-col gap-3 sm:mx-0 sm:gap-4"
        >
          {DIGITAL_DATING_WAVES.map((w, i) => {
            const isLast = i === DIGITAL_DATING_WAVES.length - 1;
            return (
              <div key={w.wave} className="relative">
                <div
                  data-testid={`wave-card-${i + 1}`}
                  className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[160px_1fr_auto] sm:items-baseline sm:gap-8 sm:p-7"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      {w.wave}
                    </span>
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">
                      {w.era}
                    </span>
                  </div>
                  <div>
                    <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                      {w.label}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground sm:text-[15px]">
                      {w.examples.join(" · ")}
                    </div>
                  </div>
                  <div className="text-sm font-medium tracking-tight text-foreground sm:text-right sm:text-[15px]">
                    {w.innovation}
                  </div>
                </div>
                {!isLast && (
                  <div
                    aria-hidden="true"
                    className="flex justify-center py-2 text-muted-foreground/50"
                  >
                    <svg
                      width="12"
                      height="14"
                      viewBox="0 0 12 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 0V13M6 13L1 8M6 13L11 8"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Narrative — supports the framework */}
        {/* First Wave */}
        <div className="pt-4" data-testid="wave-narrative-first">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            First Wave · 1995 – 2012
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Search-Based Digital Dating
          </h3>
        </div>
        <p>
          The first wave began in the mid-to-late 1990s with platforms
          like Match.com and, later, eHarmony. Their defining innovation
          was making introductions searchable. For the first time,
          meeting someone new no longer depended on the people already
          within a person&apos;s immediate social circle.
        </p>
        <p>
          Access expanded dramatically. Members who lived in smaller
          cities, worked non-traditional hours, or had moved away from
          the communities that once shaped their social lives suddenly
          had access to a much larger pool of potential partners.
        </p>
        <p className="text-foreground">
          For nearly fifteen years, search defined digital dating.
        </p>

        {/* Second Wave */}
        <div className="pt-4" data-testid="wave-narrative-second">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Second Wave · 2012 – Present
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Swipe-Based Digital Dating
          </h3>
        </div>
        <p>
          The second wave began in 2012 with Tinder. Rather than asking
          people to search, Tinder simplified discovery through
          swipe-based interactions. The industry adopted the mechanic
          quickly. Bumble, Hinge, and a wide range of niche applications
          built on the same underlying model.
        </p>
        <p>
          The result was faster, more engaging, and more accessible than
          anything that preceded it. Friction dropped. Engagement rose.
          Meeting new people online began to feel effortless.
        </p>
        <p>
          But while the experience changed, the underlying responsibility
          did not. Members still built profiles, still searched, still
          evaluated potential matches, still initiated conversations, and
          still determined compatibility for themselves.
        </p>
        <p className="text-foreground">The technology improved.</p>
        <p className="text-foreground">
          The underlying model did not fundamentally change.
        </p>

        {/* Third Wave */}
        <div className="pt-4" data-testid="wave-narrative-third">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Third Wave · Emerging
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Relationship Networks
          </h3>
        </div>
        <p>
          A third wave now appears to be emerging: the relationship
          network. Rather than asking members to continually search,
          browse, and evaluate on their own, this generation shifts much
          of that ongoing work from the individual to the platform.
        </p>
        <p>
          Members invest once in a thoughtful onboarding process. The
          platform then performs the ongoing matching in the background.
          The objective is no longer to maximize activity within the
          product. It is to reduce uncertainty before introductions
          occur, so that meaningful time can be spent on the
          relationships themselves.
        </p>
        <PullQuote testId="market-shift-quote">
          First wave expanded access. Second wave reduced friction. The
          third wave reduces uncertainty.
        </PullQuote>

        {/* Broader infrastructure */}
        <div className="pt-4" data-testid="wave-narrative-infrastructure">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Beyond software
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Relationship infrastructure is broader than a platform.
          </h3>
        </div>
        <p>
          We also believe that modern relationship infrastructure
          extends beyond software. Technology is only one component.
          Meaningful relationships ultimately happen in communities,
          not in feeds.
        </p>
        <p>
          A more complete view of relationship infrastructure includes
          trusted meetup locations, local businesses, community
          organizations, and the everyday environments that encourage
          people to move from digital introductions into real-world
          relationships.
        </p>

        {/* Approved closing paragraph */}
        <p className="text-foreground">
          We believe the third wave of digital dating won&apos;t be
          defined by helping people spend more time on a platform. It
          will be defined by helping them spend less time searching and
          more time building meaningful relationships. Technology should
          become the bridge to authentic human connection, not the
          destination itself. We believe that shift creates
          opportunities not only for HAEVN, but for the broader
          ecosystem of organizations that help strengthen local
          communities.
        </p>
      </ChapterSection>

      {/* 04 · Infrastructure for Modern Relationships */}
      <ChapterSection
        id="infrastructure"
        number={sectionNumber("infrastructure")}
        eyebrow="Infrastructure"
        title="Infrastructure for modern relationships."
        question="Why is this bigger than dating?"
      >
        <p>
          Every important aspect of society eventually develops
          infrastructure. Certain activities become too significant to
          leave entirely to chance.
        </p>

        {/* Precedents visual — five domains, five infrastructures */}
        <div
          data-testid="infrastructure-precedents"
          className="not-prose mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5"
        >
          {[
            { domain: "Transportation", label: "Roads &\u00a0bridges" },
            { domain: "Commerce", label: "Financial networks" },
            { domain: "Information", label: "The internet" },
            { domain: "Professional", label: "LinkedIn" },
            {
              domain: "Relationships",
              label: "HAEVN",
              emerging: true,
            },
          ].map((p) => (
            <div
              key={p.domain}
              data-testid={`precedent-${p.domain.toLowerCase()}`}
              className={`flex flex-col gap-2 rounded-lg border p-4 sm:p-5 ${
                p.emerging
                  ? "border-foreground/30 bg-surface"
                  : "border-border bg-surface/50"
              }`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {p.domain}
              </div>
              <div className="text-sm font-medium tracking-tight text-foreground sm:text-[15px]">
                {p.label}
              </div>
              {p.emerging && (
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  Emerging
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-foreground">Relationships are approaching that same point.</p>
        <p>
          For most of history, relationship infrastructure was largely
          physical. Communities themselves provided the mechanisms
          through which people met, built trust, and established
          long-term relationships. Those systems weren&apos;t designed
          — they evolved. Over time, many weakened. The result wasn&apos;t
          a decline in people&apos;s desire for relationships. It was a
          decline in the systems that made relationships easier to
          begin.
        </p>

        {/* Search vs Infrastructure contrast — the section's central distinction */}
        <div
          data-testid="search-vs-infrastructure"
          className="not-prose mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4"
        >
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/50 p-6 sm:p-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Search
            </div>
            <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              The individual carries the burden.
            </div>
            <p className="text-sm leading-relaxed text-foreground/80 sm:text-[15px]">
              Search made introductions accessible. It kept the ongoing
              work of evaluation, initiation, and judgment with the
              person.
            </p>
          </div>
          <div className="flex flex-col gap-3 rounded-lg border border-foreground/30 bg-surface p-6 sm:p-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Infrastructure
            </div>
            <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              The system does more of the work.
            </div>
            <p className="text-sm leading-relaxed text-foreground/80 sm:text-[15px]">
              Members make one thoughtful investment. The infrastructure
              takes on the ongoing responsibility of identifying
              meaningful introductions.
            </p>
          </div>
        </div>

        <PullQuote testId="infrastructure-quote">
          The individual no longer manages the search. The infrastructure
          manages the introductions.
        </PullQuote>

        <p>
          Most people compare HAEVN to other dating platforms because
          that is the visible application they encounter first. A more
          useful comparison is LinkedIn. LinkedIn didn&apos;t become
          valuable by digitizing résumés. It became valuable by
          standardizing professional identity and dramatically improving
          the process of professional introduction.
        </p>
        <p>
          HAEVN performs a similar role in a different domain. The
          comparison is not about features. It is about function.
          LinkedIn became infrastructure for professional introductions.
          HAEVN is building infrastructure for modern personal
          introductions.
        </p>
        <p className="text-foreground">
          That distinction is the category.
        </p>
      </ChapterSection>

      {/* 05 · Early Market Validation */}
      <ChapterSection
        id="what-weve-learned"
        number={sectionNumber("what-weve-learned")}
        eyebrow="Early Market Validation"
        title="The market is telling us something."
        question="Has the market demonstrated meaningful interest in this model?"
      >
        <p>
          The clearest signal available to any early-stage company is not
          enthusiasm. It is behavior. What follows is a summary of what
          people have consistently chosen to do inside HAEVN&apos;s early
          markets — and what those behaviors suggest about the readiness
          of the market for a more intentional approach.
        </p>

        {/* Part 1 — Market Engagement */}
        <div className="pt-6" data-testid="market-engagement">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Part 1 · Market Engagement
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            How people are behaving inside HAEVN.
          </h3>
          <div
            className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6"
            data-testid="kpi-grid"
          >
            {MARKET_ENGAGEMENT_KPIS.map((kpi, i) => (
              <div
                key={kpi.id}
                data-testid={`kpi-card-${kpi.id}`}
                className="flex flex-col gap-4 border-t border-border pt-6"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  KPI {i + 1}
                </div>
                <div className="text-2xl font-medium tracking-tight text-foreground sm:text-[26px] leading-[1.15]">
                  {kpi.headline}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground sm:text-[15px] sm:leading-[1.65]">
                  {kpi.body}
                </p>
              </div>
            ))}
          </div>

          <p
            data-testid="kpi-source-note"
            className="mt-8 max-w-3xl text-xs leading-relaxed text-muted-foreground sm:text-[13px]"
          >
            Source: HAEVN internal reporting. Figures reflect running
            averages since the network went live. These are early
            indicators of engagement and advocacy — not proof of
            successful relationships or validated nationwide
            performance.
          </p>
        </div>

        {/* Part 2 — Who We're Attracting */}
        <div className="pt-10" data-testid="who-attracting">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Part 2 · Who We&apos;re Attracting
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            A broad, organic audience.
          </h3>
          <p className="mt-4">
            HAEVN&apos;s membership reflects broad organic appeal. Without
            targeting any specific demographic or relationship type, the
            platform naturally attracts a diverse mix of ages, identities,
            orientations, and relationship intentions.
          </p>

          {/* Gender */}
          <div className="mt-12" data-testid="chart-gender">
            <h4 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Gender
            </h4>
            <div className="mt-6">
              <SegmentedBar
                segments={GENDER_SEGMENTS}
                testId="chart-gender-bar"
                ariaLabel="Gender composition of HAEVN members"
              />
            </div>
          </div>

          {/* Age */}
          <div className="mt-12" data-testid="chart-age">
            <h4 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Age
            </h4>
            <div className="mt-6">
              <HorizontalBarChart
                data={AGE_DISTRIBUTION}
                labelClassName="w-20"
                testId="chart-age-bars"
                ariaLabel="Age distribution of HAEVN members"
              />
            </div>
          </div>

          {/* Sexual Orientation */}
          <div className="mt-12" data-testid="chart-orientation">
            <h4 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Sexual Orientation
            </h4>
            <div className="mt-6">
              <SegmentedBar
                segments={ORIENTATION_SEGMENTS}
                testId="chart-orientation-bar"
                ariaLabel="Sexual orientation composition of HAEVN members"
              />
            </div>
          </div>

          {/* Relationship Intent */}
          <div className="mt-12" data-testid="chart-goals">
            <h4 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Relationship Intent
            </h4>
            <div
              className="mt-8 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8"
              data-testid="chart-goals-callouts"
            >
              {RELATIONSHIP_INTENTIONS.map((item) => (
                <div
                  key={item.id}
                  data-testid={`goal-callout-${item.id}`}
                  className="border-t border-border pt-5"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    {item.label}
                  </div>
                  <div className="mt-3 text-4xl font-medium tracking-tightest text-foreground sm:text-[42px] leading-none">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Members may select multiple relationship intentions.
            </p>
          </div>
        </div>

        {/* Part 3 — Why This Matters */}
        <div className="pt-10" data-testid="why-this-matters">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Part 3 · Why This Matters
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            A market signaling readiness for intentionality.
          </h3>
        </div>

        {/* Three-signal recap — visual anchor for the closing conclusion */}
        <div
          data-testid="behavior-signals-recap"
          className="not-prose mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4"
        >
          {[
            { verb: "Invest", body: "meaningful time" },
            { verb: "Return", body: "to complete onboarding" },
            { verb: "Share", body: "HAEVN with others" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`signal-recap-${i + 1}`}
              className="flex flex-col gap-1 rounded-lg border border-border bg-surface/50 p-5 sm:p-6"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Signal {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                {s.verb}
              </div>
              <div className="text-sm text-muted-foreground sm:text-[15px]">
                {s.body}
              </div>
            </div>
          ))}
        </div>

        <p className="text-foreground">
          Taken together, these behaviors tell a compelling story. People
          are willing to invest meaningful time, return to complete
          thoughtful onboarding experiences, and voluntarily introduce
          others to HAEVN. These are not simply product metrics. They are
          early indicators that the market is receptive to a more
          intentional approach to modern relationships.
        </p>
      </ChapterSection>

      {/* 06 · Why This Matters to Organizations */}
      <ChapterSection
        id="why-matters"
        number={sectionNumber("why-matters")}
        eyebrow="Why This Matters to Organizations"
        title="From consumer behavior to strategic opportunity."
        question="Why should an organization care?"
      >
        <p>
          The behavioral signals we&apos;ve observed among consumers
          matter in their own right. They also suggest something larger
          — an opportunity that connects directly to objectives many
          organizations are already pursuing.
        </p>
        <p>
          When consumer behavior changes, new categories often emerge.
          Each represents more than a new product. They reflect changing
          consumer expectations that ultimately create new opportunities
          for organizations willing to adapt alongside them.
        </p>

        {/* Precedent grid — behavior shifts that created new categories */}
        <div
          data-testid="behavior-precedents"
          className="not-prose mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4"
        >
          {[
            {
              before: "Search-based dating",
              after: "Swipe",
              note: "Reduced friction",
            },
            {
              before: "Taxis",
              after: "Ride sharing",
              note: "On-demand transportation",
            },
            {
              before: "DVDs & cable",
              after: "Streaming",
              note: "On-demand media",
            },
          ].map((p, i) => (
            <div
              key={i}
              data-testid={`behavior-precedent-${i + 1}`}
              className="flex flex-col gap-2 rounded-lg border border-border bg-surface/50 p-5 sm:p-6"
            >
              <div className="text-sm text-muted-foreground line-through decoration-muted-foreground/50 sm:text-[15px]">
                {p.before}
              </div>
              <div className="text-base font-medium tracking-tight text-foreground sm:text-lg">
                {p.after}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {p.note}
              </div>
            </div>
          ))}
        </div>

        <p>
          We believe digital dating is approaching a similar transition.
          People are increasingly looking for a more intentional approach
          to building relationships, and our early market signals suggest
          they are willing to invest meaningful time when they believe
          the outcome is worth it.
        </p>
        <p>
          For organizations already investing in brand affinity, community
          engagement, and authentic consumer experiences, this moment
          creates an opportunity to advance those same objectives through
          a new kind of participation. Rather than viewing organizations
          as sponsors, HAEVN views them as Growth Partners. Together, we
          have an opportunity to help establish infrastructure for modern
          relationships while advancing many of the community, brand, and
          market objectives organizations are already investing in.
        </p>
        <p>
          The remainder of this Executive Brief explains how that model
          works, why we believe it creates value for both organizations,
          and how the same philosophy that guides HAEVN&apos;s approach
          to introductions also guides HAEVN&apos;s approach to
          partnerships.
        </p>

        {/* Strategic pull quote — the bridge into the Growth Partner model */}
        <figure
          data-testid="strategic-callout"
          className="not-prose relative mt-12 sm:mt-16"
        >
          <div className="border-y border-foreground/25 py-10 sm:py-14">
            <span
              aria-hidden="true"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              An Invitation
            </span>
            <blockquote className="mt-4 max-w-3xl">
              <p className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]">
                Give your audience something valuable. Give people
                another reason to value your brand.
              </p>
            </blockquote>
          </div>
        </figure>
      </ChapterSection>

      {/* 07 · Why Organizations Invest Beyond Awareness */}
      <ChapterSection
        id="invest-beyond-awareness"
        number={sectionNumber("invest-beyond-awareness")}
        eyebrow="Modern Marketing Priorities"
        title="Why organizations invest beyond awareness."
        question="Where does HAEVN fit within our existing strategy?"
      >
        <p>
          Marketing has evolved well beyond generating awareness.
        </p>
        <p>
          Today&apos;s organizations are expected to build stronger
          communities, create authentic engagement, strengthen emotional
          connections, generate advocacy, and create experiences people
          genuinely choose to participate in.
        </p>

        {/* Visual framework — the evolution of marketing priorities */}
        <div
          data-testid="marketing-evolution"
          className="not-prose mt-6"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            The Evolution of Marketing Priorities
          </div>
          <ol
            data-testid="marketing-evolution-stages"
            className="mt-4 flex flex-col gap-3 sm:gap-3"
          >
            {[
              { label: "Advertising", supports: false },
              { label: "Awareness", supports: false },
              { label: "Engagement", supports: false },
              { label: "Community", supports: true },
              { label: "Brand Affinity", supports: true },
              { label: "Meaningful Relationships", supports: true },
            ].map((s, i, arr) => {
              const isLast = i === arr.length - 1;
              return (
                <li key={s.label} className="flex flex-col">
                  <div
                    data-testid={`evolution-stage-${i + 1}`}
                    className={`grid grid-cols-[48px_1fr_auto] items-center gap-4 rounded-lg border px-5 py-4 sm:gap-6 sm:px-6 sm:py-5 ${
                      s.supports
                        ? "border-foreground/30 bg-surface"
                        : "border-border bg-surface/40"
                    }`}
                  >
                    <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`text-base font-medium tracking-tight sm:text-lg ${
                        s.supports ? "text-foreground" : "text-foreground/70"
                      }`}
                    >
                      {s.label}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {s.supports ? "Where HAEVN fits" : ""}
                    </span>
                  </div>
                  {!isLast && (
                    <div
                      aria-hidden="true"
                      className="flex justify-start py-1 pl-6 text-muted-foreground/40 sm:pl-7"
                    >
                      <ExchangeDownArrow />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <p>
          Those objectives have given rise to everything from experiential
          marketing and creator partnerships to community activations,
          sponsorships, and purpose-driven initiatives. While the tactics
          vary, they are all attempting to accomplish something similar:
          building stronger relationships between organizations and the
          people they serve.
        </p>

        <p className="text-foreground">
          We believe HAEVN represents a new way to pursue those same
          objectives.
        </p>

        <p>
          Rather than creating another advertising channel, HAEVN
          creates opportunities for organizations to participate in one
          of the most meaningful and enduring aspects of people&apos;s
          lives: building relationships.
        </p>
        <p>
          That distinction is what makes the Growth Partner model
          fundamentally different from traditional sponsorships or media
          buys.
        </p>
      </ChapterSection>

      {/* 08 · The Strategic Exchange of Value */}
      <ChapterSection
        id="exchange"
        number={sectionNumber("exchange")}
        eyebrow="The Strategic Exchange of Value"
        title="Why this partnership works."
        question="Why does this partnership work?"
      >
        <p>
          Your organization contributes trusted reach and community
          relationships. HAEVN contributes the matchmaking experience.
          Together, we create an opportunity to deliver audience value,
          strengthen local participation, and develop a measurable
          collaboration.
        </p>
        <p>
          Growth Partners and Community Outreach Partners (COPs) are
          distinct, complementary roles. Growth Partners lead
          audience-level introductions through their own channels. COPs
          are deployed by HAEVN to accelerate promising local
          communities. A single organization may relate to either role;
          the two are not interchangeable names for the same thing.
        </p>

        {/* Two-column: what your organization contributes vs. gains */}
        <div
          data-testid="contribution-grid"
          className="not-prose mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6"
        >
          <div
            data-testid="partner-contributes"
            className="flex flex-col gap-5 rounded-lg border border-border bg-surface/50 p-6 sm:p-7"
          >
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                What Your Organization Contributes
              </span>
              <span className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                Trusted reach and relationships.
              </span>
            </div>
            <ul className="flex flex-col gap-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Access to relevant audiences through its own channels
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Brand credibility and community relationships
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Agreed communications or activation support
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Storytelling capabilities and local knowledge
              </li>
            </ul>
            <p className="text-xs leading-relaxed text-muted-foreground sm:text-[13px]">
              Audience access means partner-led outreach through the
              partner&apos;s own channels. It does not require sharing
              customer lists or private member information.
            </p>
          </div>

          <div
            data-testid="partner-gains"
            className="flex flex-col gap-5 rounded-lg border border-foreground/30 bg-surface p-6 sm:p-7"
          >
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                What Your Organization Gains
              </span>
              <span className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                A useful offering and measurable participation.
              </span>
            </div>
            <ul className="flex flex-col gap-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                A relevant service to introduce to its audience
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Agreed recognition for its participation
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Opportunities for local engagement and authentic storytelling
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Agreed evidence to inform future investment
              </li>
            </ul>
          </div>
        </div>

        {/* What HAEVN contributes — retained but repositioned */}
        <div
          data-testid="haevn-contributes"
          className="not-prose mt-6 flex flex-col gap-5 rounded-lg border border-border bg-surface/40 p-6 sm:p-7"
        >
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              HAEVN Contributes
            </span>
            <span className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              The matchmaking service and community infrastructure.
            </span>
          </div>
          <ul className="grid grid-cols-1 gap-3 text-sm leading-relaxed text-foreground/85 sm:grid-cols-2 sm:text-[15px]">
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
              The modern matchmaking experience
            </li>
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
              National marketing that identifies where demand emerges
            </li>
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
              Local community infrastructure and COP deployment
            </li>
            <li className="flex gap-3">
              <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
              Aggregate participation insights and collaboration outcomes
            </li>
          </ul>
          <p className="text-xs leading-relaxed text-muted-foreground sm:text-[13px]">
            Aggregate participation insights and collaboration outcomes
            help both organizations understand what is working. Growth
            Partners do not receive individual survey answers, private
            relationship preferences, identifiable matching information,
            or direct access to members.
          </p>
        </div>

        {/* Editorial pull quote */}
        <figure
          data-testid="exchange-callout"
          className="not-prose relative mt-12 sm:mt-16"
        >
          <div className="border-y border-foreground/25 py-10 sm:py-14">
            <blockquote className="max-w-3xl">
              <p className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]">
                Neither organization is being asked to do what the other
                already does well.
              </p>
            </blockquote>
          </div>
        </figure>

        <p>
          The Growth Partner model creates value by combining
          complementary capabilities rather than duplicating existing
          ones. What each organization already does well is exactly what
          it contributes.
        </p>
      </ChapterSection>

      {/* 09 · A Different Kind of Marketing Opportunity */}
      <ChapterSection
        id="marketing-opportunity"
        number={sectionNumber("marketing-opportunity")}
        eyebrow="A Different Kind of Opportunity"
        title="A partnership built around audience value and local participation."
        question="Why should this be viewed differently from a sponsorship?"
      >
        <p>
          Traditional partnerships often begin with a familiar set of
          questions: What exposure will this create? How many
          impressions will we receive? What is the immediate return?
          Those are legitimate considerations and remain part of an
          honest conversation.
        </p>
        <p className="text-foreground">
          A Growth Partnership begins with an additional question.
        </p>
        <p className="text-foreground">
          What are we helping our audience access — and what does the
          response tell us?
        </p>
        <p>
          Visibility can be one component of the collaboration,
          alongside useful audience engagement and community activity.
          The intention is that all three inform whether the partnership
          expands.
        </p>

        {/* Editorial callout — the central idea of the section */}
        <figure
          data-testid="infrastructure-callout"
          className="not-prose relative mt-8 sm:mt-12"
        >
          <div className="border-y border-foreground/25 py-10 sm:py-14">
            <span
              aria-hidden="true"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              The Central Idea
            </span>
            <blockquote className="mt-4 max-w-3xl">
              <p className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]">
                Give your audience something valuable. Give people
                another reason to value your brand.
              </p>
            </blockquote>
          </div>
        </figure>

        <p>
          Every additional participant strengthens the local network.
          Every successful local activation creates knowledge that
          improves the next one. Every organization that joins helps
          extend the story of how the category is developing. Marketing
          activations accelerate an existing service; they do not
          unlock participation. Members can enter the process from
          anywhere without waiting for a market threshold. HAEVN
          concentrates additional resources — paid marketing, Meetup
          Spots, local creators, PR, events, and partnerships — in
          Focus Cities where local participation demonstrates
          momentum. Focus City is an internal investment designation,
          not a condition of member eligibility.
        </p>

        {/* Sub-section: how the model earns commitment */}
        <div className="pt-6" data-testid="modest-beginning">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            How the partnership begins
          </div>
          <h3 className="mt-2 text-xl font-medium tracking-tight text-foreground sm:text-2xl">
            Small first steps, on purpose.
          </h3>
        </div>
        <p>
          The Growth Partner model intentionally begins with a modest,
          low-risk collaboration that allows both organizations to learn
          before making larger commitments. That structure is not a
          tactical choice. It is a reflection of philosophy.
        </p>
        <p className="text-foreground">
          Reduce uncertainty first. Then increase commitment.
        </p>
        <p>
          It is the same principle that guides how HAEVN introduces
          people to one another. Rather than asking either organization
          to commit to a large-scale program on day one, the model
          creates observable evidence at each step so that the next step
          is grounded in what has actually been learned — not in what
          was projected at the outset.
        </p>
      </ChapterSection>

      {/* 10 · Built Around Existing Marketing Priorities */}
      <ChapterSection
        id="framework"
        number={sectionNumber("framework")}
        eyebrow="Marketing Priorities"
        title="Built around existing marketing priorities."
        question="How does this fit within what we already prioritize?"
      >
        <p>
          The Growth Partner model was intentionally designed to align
          with the priorities sophisticated marketing organizations
          already have — not to require them to create new ones. Five
          principles guided that design.
        </p>

        {/* Editorial principle cards */}
        <div
          data-testid="marketing-principles"
          className="not-prose mt-4 flex flex-col gap-3 sm:gap-4"
        >
          {MARKETING_PRINCIPLES.map((p, i) => (
            <div
              key={p.id}
              data-testid={`principle-card-${p.id}`}
              className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[64px_1fr] sm:items-baseline sm:gap-8 sm:p-7"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Principle {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                  {p.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-foreground">
          The same philosophy that guides HAEVN&apos;s approach to
          relationships also guides its approach to partnerships: reduce
          uncertainty, demonstrate value, then earn the next step.
        </p>
      </ChapterSection>

      {/* 11 · Why HAEVN */}
      <ChapterSection
        id="why-haevn"
        number={sectionNumber("why-haevn")}
        eyebrow="Why HAEVN"
        title="Why HAEVN."
        question="Why HAEVN, and why now?"
      >
        <p>
          HAEVN is not the only organization that will ever build a
          relationship network. It is, however, uniquely positioned to
          help define this emerging category — because the philosophy,
          the product, and the partnership model all follow from a
          single, consistent idea.
        </p>

        {/* Four editorial themes */}
        <div
          data-testid="why-haevn-themes"
          className="not-prose mt-4 flex flex-col gap-3 sm:gap-4"
        >
          {/* Theme 01 — A Different Founding Question */}
          <div
            data-testid="theme-founding-question"
            className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[64px_1fr] sm:items-baseline sm:gap-8 sm:p-7"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              01
            </div>
            <div>
              <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                A Different Founding Question
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                HAEVN did not begin by asking how to build a better
                dating app. It began by asking a different question:
                what if finding compatible introductions became the
                platform&apos;s responsibility instead of the
                individual&apos;s? That question continues to shape
                every major decision the company makes.
              </p>
            </div>
          </div>

          {/* Theme 02 — Early Evidence */}
          <div
            data-testid="theme-early-evidence"
            className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[64px_1fr] sm:items-baseline sm:gap-8 sm:p-7"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              02
            </div>
            <div>
              <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                Early Evidence
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                People are already telling us the philosophy is
                resonating. They invest meaningful time in the
                onboarding process. They return to complete it when
                they can&apos;t finish in one sitting. They share HAEVN
                with others because they believe in what it represents.
                These behaviors are early — but they are consistent.
              </p>
            </div>
          </div>

          {/* Theme 03 — Consistency of Vision */}
          <div
            data-testid="theme-consistency"
            className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[64px_1fr] sm:items-baseline sm:gap-8 sm:p-7"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              03
            </div>
            <div>
              <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                Consistency of Vision
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                The same principles that guide product design also
                guide partnerships, expansion, and community
                development. Reduce uncertainty. Demonstrate value.
                Earn the next step. That consistency is one of the
                company&apos;s greatest strengths — and one of the
                clearest signals a Growth Partner can use to assess
                long-term direction.
              </p>
            </div>
          </div>

          {/* Theme 04 — Designed for the Long Term */}
          <div
            data-testid="theme-long-term"
            className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:grid-cols-[64px_1fr] sm:items-baseline sm:gap-8 sm:p-7"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              04
            </div>
            <div>
              <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                Designed for the Long Term
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                HAEVN is building infrastructure, not launching another
                consumer application. The objective is to establish
                long-term trust, strengthen communities, and reduce
                uncertainty wherever meaningful relationships begin.
                Infrastructure work rewards patience, discipline, and
                consistency — the qualities the company was built
                around.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-10 text-foreground">
          A clear vision, a differentiated approach, and a philosophy
          that is consistent across the product, the partnership model,
          and the company itself. That is the case for HAEVN.
        </p>
      </ChapterSection>

      {/* 12 · A Partnership Designed to Earn the Next Step */}
      <ChapterSection
        id="partnership"
        number={sectionNumber("partnership")}
        eyebrow="The Partnership"
        title="Designed to earn the next step."
        question="How does the partnership evolve over time?"
      >
        <p>
          Most partnerships begin by negotiating commitments. The Growth
          Partner model begins by earning them.
        </p>
        <p>
          Every phase exists to answer a specific question before either
          organization is asked to increase its investment. It is the
          same philosophy that guides HAEVN&apos;s approach to
          introductions.
        </p>
        <p className="text-foreground">
          Reduce uncertainty. Demonstrate value. Earn the next step.
        </p>
        <p>
          An initial collaboration may reach a broad audience or a
          relevant local community. HAEVN and the Growth Partner assess
          where participation develops and whether it produces
          meaningful engagement. Deeper local activity and broader
          investment follow demonstrated momentum. Not every Growth
          Partner needs to operate nationally, and partner participation
          is not required before members in a community can use HAEVN.
        </p>

        {/* Elevated editorial callout — the dominant visual takeaway */}
        <figure
          data-testid="partnership-callout"
          className="not-prose relative mt-10 sm:mt-12"
        >
          <div className="border-y border-foreground/25 py-10 sm:py-14">
            <span
              aria-hidden="true"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              The Governing Principle
            </span>
            <blockquote className="mt-4 max-w-3xl">
              <p className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]">
                Every stage should earn the next. Every expansion should
                be supported by evidence.
              </p>
            </blockquote>
          </div>
        </figure>

        {/* The five-phase roadmap */}
        <div
          data-testid="partnership-roadmap"
          className="not-prose relative mt-10"
        >
          {/* Vertical spine */}
          <div
            aria-hidden="true"
            className="absolute left-[19px] top-4 bottom-4 w-px bg-border sm:left-[23px]"
          />

          <ol className="flex flex-col gap-4 sm:gap-5">
            {PARTNERSHIP_PHASES.map((phase, i) => (
              <li
                key={phase.id}
                data-testid={`phase-card-${phase.id}`}
                className="relative flex items-start gap-5 sm:gap-6"
              >
                {/* Numbered node */}
                <div
                  className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-background sm:h-12 sm:w-12 ${
                    i === PARTNERSHIP_PHASES.length - 1
                      ? "border-foreground/30"
                      : "border-border"
                  }`}
                >
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground sm:text-xs">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Phase card */}
                <div className="min-w-0 flex-1 rounded-lg border border-border bg-surface/50 p-6 sm:p-7">
                  <h3 className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                    {phase.title}
                  </h3>
                  <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6">
                    <div>
                      <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        Objective
                      </dt>
                      <dd className="mt-1 text-sm font-medium tracking-tight text-foreground sm:text-[15px]">
                        {phase.objective}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        Question Answered
                      </dt>
                      <dd className="mt-1 text-sm font-medium tracking-tight text-foreground sm:text-[15px]">
                        {phase.question}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-4 text-sm leading-relaxed text-foreground/85 sm:text-[15px] sm:leading-[1.65]">
                    {phase.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <p className="mt-10">
          Strong relationships — whether between individuals or
          organizations — are built through progressive trust. Neither
          should begin by asking participants to assume unnecessary
          risk.
        </p>
      </ChapterSection>

      {/* 13 · Defining Success */}
      <ChapterSection
        id="success"
        number={sectionNumber("success")}
        eyebrow="Defining Success"
        title="Three dimensions, aligned."
        question="How would we know it worked?"
      >
        <p>
          The strongest partnerships create value for everyone involved.
          HAEVN evaluates success across three dimensions: value created
          for the Growth Partner, value created for HAEVN, and value
          created for the communities both organizations serve.
        </p>
        <p>
          The lists below are measures that can be agreed for a given
          collaboration. They are measures to evaluate — not results
          already achieved. Specific measures, sources, and reporting
          cadences are agreed together before work begins.
        </p>

        {/* Practical measures — Growth Partner first, HAEVN, then Communities */}
        <div
          data-testid="success-measures"
          className="not-prose mt-6 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3"
        >
          <div
            data-testid="measures-partner"
            className="flex flex-col gap-4 rounded-lg border border-foreground/30 bg-surface p-6 sm:p-7"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              For the Growth Partner
            </div>
            <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Measures of audience and brand value
            </div>
            <ul className="flex flex-col gap-2 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Participation attributable to partner activity
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Engagement with partner communications and local initiatives
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Relevant storytelling and earned media
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Changes in brand affinity or trust where measured through an agreed method
              </li>
            </ul>
          </div>

          <div
            data-testid="measures-haevn"
            className="flex flex-col gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:p-7"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              For HAEVN
            </div>
            <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Measures of service participation
            </div>
            <ul className="flex flex-col gap-2 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Completed surveys by metro and acquisition source
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Rate of local membership growth
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Referral-attributed survey completions
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Match Monday participation
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Matches and Recommendations generated
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Progress from introductions toward meeting
              </li>
            </ul>
          </div>

          <div
            data-testid="measures-communities"
            className="flex flex-col gap-4 rounded-lg border border-border bg-surface/50 p-6 sm:p-7"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              For the Communities
            </div>
            <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
              Measures of local health
            </div>
            <ul className="flex flex-col gap-2 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Sustained local participation
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Growth in relevant matching opportunities
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Introductions progressing toward real-world connection
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                Local participation and matching opportunities grow across more communities
              </li>
            </ul>
          </div>
        </div>

        <p
          data-testid="success-footnote"
          className="mt-6 max-w-3xl text-xs leading-relaxed text-muted-foreground sm:text-[13px]"
        >
          Specific measures, sources, methodologies, and reporting
          cadences are agreed together before work begins. This section
          does not imply performance targets, market graduation
          thresholds, or a numerical momentum score.
        </p>

        {/* Elevated closing principle */}
        <figure
          data-testid="success-callout"
          className="not-prose relative mt-12 sm:mt-16"
        >
          <div className="border-y border-foreground/25 py-10 sm:py-14">
            <span
              aria-hidden="true"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              What Sustains
            </span>
            <blockquote className="mt-4 max-w-3xl">
              <p className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]">
                Success is defined together, evaluated together, and
                grows where the evidence supports it.
              </p>
            </blockquote>
          </div>
        </figure>

        <p>
          Local participation and matching opportunities grow across
          more communities as evidence supports investment. Each
          successful collaboration contributes learnings to the next.
          Growth is intentional — directed to where the response
          justifies it.
        </p>
      </ChapterSection>

      {/* 14 · Executive Questions */}
      <ChapterSection
        id="questions"
        number={sectionNumber("questions")}
        eyebrow="Executive Questions"
        title="Questions every executive should ask."
        question="What have I not yet asked?"
      >
        <p>
          Every organization evaluates strategic opportunities
          differently. Rather than attempting to anticipate every
          possible consideration throughout this Executive Brief,
          we&apos;ve included the questions we believe thoughtful
          executives are most likely to ask before deciding whether a
          Growth Partner relationship aligns with their organization&apos;s
          priorities.
        </p>

        <div className="mt-10 flex flex-col gap-3" data-testid="questions-list">
          {EXECUTIVE_QUESTIONS.map((item) => (
            <Expandable
              key={item.id}
              title={item.q}
              testId={`question-${item.id}`}
            >
              <p className="max-w-3xl leading-relaxed">{item.a}</p>
            </Expandable>
          ))}
        </div>
      </ChapterSection>

      {/* 15 · An Invitation to Build */}
      <ChapterSection
        id="invitation"
        number={sectionNumber("invitation")}
        eyebrow="An Invitation to Build"
        title="What could this create for your audience?"
        question="Where does this go from here?"
      >
        {/* Closing statement */}
        <p
          data-testid="closing-statement"
          className="text-2xl font-medium tracking-tight text-foreground sm:text-3xl sm:leading-[1.3]"
        >
          Start with the people you want to serve and the objective
          you want to advance. Together, we can define a focused first
          collaboration, agree what each organization contributes, and
          establish how we will evaluate the response.
        </p>

        {/* Founder sign-off — photo, signature, attribution, email */}
        <div
          data-testid="founder-signoff"
          className="not-prose mt-14 flex flex-col gap-8 border-t border-border pt-10 sm:mt-20 sm:flex-row sm:items-start sm:gap-10"
        >
          <img
            src="/rik-foote.jpg"
            alt="Rik Foote, Founder & CEO of HAEVN"
            data-testid="founder-photo-close"
            width={800}
            height={1000}
            className="h-auto w-40 shrink-0 rounded-md object-cover sm:w-48"
            loading="lazy"
          />
          <div className="flex flex-col gap-6">
            <img
              src="/signature.png"
              alt="Signature of Rik Foote"
              data-testid="founder-signature-image"
              width={230}
              height={84}
              className="-ml-1 h-auto w-40 sm:w-48 dark:invert"
              loading="lazy"
            />
            <div>
              <div className="text-lg font-medium tracking-tight text-foreground sm:text-xl">
                Rik Foote
              </div>
              <div className="text-sm text-muted-foreground sm:text-[15px]">
                Founder &amp; CEO, HAEVN
              </div>
            </div>
            <a
              href="mailto:partners@haevn.co?subject=Discuss%20a%20Growth%20Partnership"
              data-testid="closing-primary-cta"
              className="inline-flex w-fit items-center gap-2 rounded-full border border-foreground bg-foreground px-6 py-3 text-sm font-medium tracking-tight text-background transition-colors hover:bg-foreground/90 sm:text-[15px]"
            >
              Discuss a Growth Partnership
              <span aria-hidden="true">→</span>
            </a>
            <a
              href="mailto:partners@haevn.co"
              data-testid="contact-email"
              className="text-base font-medium tracking-tight text-foreground underline decoration-foreground/30 underline-offset-4 transition-colors hover:decoration-foreground sm:text-lg"
            >
              partners@haevn.co
            </a>
          </div>
        </div>
      </ChapterSection>
    </div>
  );
}
